import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Cookie, X, Settings, Shield } from 'lucide-react'
import { Button } from '@/components/ui'

type ConsentState = 'all' | 'essential-only' | 'custom' | null

interface CookiePreferences {
  essential: boolean // Always true, cannot be disabled
  analytics: boolean
  marketing: boolean
  functional: boolean
}

const CONSENT_KEY = 'uca_cookie_consent'
const PREFERENCES_KEY = 'uca_cookie_preferences'

// Check for Global Privacy Control signal
function hasGPCSignal(): boolean {
  if (typeof navigator !== 'undefined' && 'globalPrivacyControl' in navigator) {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  }
  return false
}

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  })

  // Check for existing consent on mount
  useEffect(() => {
    const existingConsent = localStorage.getItem(CONSENT_KEY)
    const existingPrefs = localStorage.getItem(PREFERENCES_KEY)
    
    if (existingConsent) {
      // User has already made a choice
      if (existingPrefs) {
        setPreferences(JSON.parse(existingPrefs))
      }
      setIsVisible(false)
    } else {
      // Check for GPC signal - if present, default to essential only
      if (hasGPCSignal()) {
        handleDeclineAll()
      } else {
        // Show banner after a short delay for better UX
        const timer = setTimeout(() => setIsVisible(true), 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [])

  const saveConsent = (state: ConsentState, prefs: CookiePreferences) => {
    localStorage.setItem(CONSENT_KEY, state || 'essential-only')
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs))
    
    // Dispatch event so other scripts can react to consent changes
    window.dispatchEvent(new CustomEvent('cookieConsentChange', { 
      detail: { consent: state, preferences: prefs } 
    }))
  }

  const handleAcceptAll = () => {
    const allEnabled: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    }
    setPreferences(allEnabled)
    saveConsent('all', allEnabled)
    setIsVisible(false)
  }

  const handleDeclineAll = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    }
    setPreferences(essentialOnly)
    saveConsent('essential-only', essentialOnly)
    setIsVisible(false)
  }

  const handleSavePreferences = () => {
    saveConsent('custom', preferences)
    setIsVisible(false)
    setShowSettings(false)
  }

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return // Cannot disable essential cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  // Allow reopening the banner from anywhere
  useEffect(() => {
    const handleOpenConsent = () => {
      setIsVisible(true)
      setShowSettings(true)
    }
    window.addEventListener('openCookieConsent', handleOpenConsent)
    return () => window.removeEventListener('openCookieConsent', handleOpenConsent)
  }, [])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="container-custom">
            <div className="glass-card-solid rounded-2xl shadow-2xl overflow-hidden">
              {!showSettings ? (
                // Main Banner
                <div className="p-6 md:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Icon and Text */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center flex-shrink-0">
                        <Cookie className="w-6 h-6 text-accent-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-white mb-2">
                          We Value Your Privacy
                        </h3>
                        <p className="text-slate-400 text-sm">
                          We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                          You can choose to accept all cookies, decline non-essential cookies, or customize your preferences.{' '}
                          <Link to="/cookies" className="text-accent-400 hover:underline">
                            Learn more
                          </Link>
                        </p>
                      </div>
                    </div>

                    {/* Buttons - CCPA Compliant: Equal prominence */}
                    <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowSettings(true)}
                        className="order-3 sm:order-1"
                      >
                        <Settings className="w-4 h-4" />
                        Cookie Settings
                      </Button>
                      {/* Decline and Accept have EQUAL styling per CCPA symmetry rules */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeclineAll}
                        className="order-2 border-white/20 hover:bg-white/10"
                      >
                        Decline All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAcceptAll}
                        className="order-1 sm:order-3 border-white/20 hover:bg-white/10"
                      >
                        Accept All
                      </Button>
                    </div>
                  </div>

                  {/* Do Not Sell Link - CCPA Required */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button
                      onClick={() => {
                        handleDeclineAll()
                      }}
                      className="text-sm text-accent-400 hover:underline flex items-center gap-2"
                    >
                      <Shield className="w-4 h-4" />
                      Do Not Sell or Share My Personal Information
                    </button>
                  </div>
                </div>
              ) : (
                // Settings Panel
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-heading font-semibold text-white">
                      Cookie Preferences
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Essential Cookies */}
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div>
                        <h4 className="font-semibold text-white">Essential Cookies</h4>
                        <p className="text-sm text-slate-400">Required for the website to function. Cannot be disabled.</p>
                      </div>
                      <div className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                        Always On
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                      <div>
                        <h4 className="font-semibold text-white">Analytics Cookies</h4>
                        <p className="text-sm text-slate-400">Help us understand how visitors interact with our website.</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={preferences.analytics}
                        onClick={() => togglePreference('analytics')}
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          preferences.analytics ? 'bg-accent-500' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </label>

                    {/* Marketing Cookies */}
                    <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                      <div>
                        <h4 className="font-semibold text-white">Marketing Cookies</h4>
                        <p className="text-sm text-slate-400">Used to deliver relevant ads and track campaign effectiveness.</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={preferences.marketing}
                        onClick={() => togglePreference('marketing')}
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          preferences.marketing ? 'bg-accent-500' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.marketing ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </label>

                    {/* Functional Cookies */}
                    <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                      <div>
                        <h4 className="font-semibold text-white">Functional Cookies</h4>
                        <p className="text-sm text-slate-400">Enable enhanced functionality like chat widgets.</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={preferences.functional}
                        onClick={() => togglePreference('functional')}
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          preferences.functional ? 'bg-accent-500' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.functional ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </label>
                  </div>

                  {/* Action Buttons - Equal prominence */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      onClick={handleDeclineAll}
                      className="flex-1 border-white/20 hover:bg-white/10"
                    >
                      Decline All
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleAcceptAll}
                      className="flex-1 border-white/20 hover:bg-white/10"
                    >
                      Accept All
                    </Button>
                    <Button
                      variant="glow"
                      onClick={handleSavePreferences}
                      className="flex-1"
                    >
                      Save Preferences
                    </Button>
                  </div>

                  <p className="text-xs text-slate-500 mt-4 text-center">
                    View our full <Link to="/cookies" className="text-accent-400 hover:underline">Cookie Policy</Link> and{' '}
                    <Link to="/privacy" className="text-accent-400 hover:underline">Privacy Policy</Link>
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Export a function to open the consent banner
export function openCookieConsent() {
  window.dispatchEvent(new CustomEvent('openCookieConsent'))
}
